/*
 * Rotation d'un modèle 3D à la souris (clic maintenu + glisser).
 *
 * Les canvas des pages produit ont `pointer-events: none` pour ne pas bloquer
 * les clics ni le scroll : on écoute donc la fenêtre, et `hitTest(x, y)` dit
 * si le pointeur est au-dessus du modèle.
 *
 * - glisser à l'horizontale : fait tourner le modèle sur lui-même (avec inertie)
 * - glisser à la verticale  : incline le modèle, qui revient doucement à plat
 * - souris et stylet seulement : au doigt, le glissement reste réservé au scroll
 *
 * Utilisation (dans le composant) :
 *   this.dragRotate = new DragRotate({ hitTest: (x, y) => ... });
 *   // à chaque image :
 *   const yaw = this.dragRotate.update(dt); // radians à ajouter à la rotation Y
 *   group.rotation.x = this.dragRotate.tilt; // inclinaison
 *   // pendant que this.dragRotate.dragging est vrai, mets la rotation auto en pause
 */

const INTERACTIVE = 'a, button, input, textarea, select, label, summary, video, iframe, [role="button"]';

export default class DragRotate {
  constructor({
    hitTest,
    sensitivity = 0.008, // radians par pixel
    maxTilt = 0.7, // inclinaison maximale (radians)
    inertia = 2.5, // plus grand = l'élan s'arrête plus vite
    interactive = INTERACTIVE, // éléments sur lesquels on ne démarre pas de glissement
    onCursor = null, // (valeur) => ... pour gérer le curseur soi-même
    maxVelocity = 20, // rad/s
  }) {
    this.hitTest = hitTest;
    this.sensitivity = sensitivity;
    this.maxTilt = maxTilt;
    this.inertia = inertia;
    this.maxVelocity = maxVelocity;
    this.interactive = interactive;
    this.onCursor = onCursor;

    this.dragging = false;
    this.justDragged = false; // vrai juste après un vrai glissement (pour ignorer le clic qui suit)
    this.travel = 0; // distance parcourue pendant le glissement (px)
    this.tilt = 0; // inclinaison appliquée (lissée)
    this.tiltTarget = 0;
    this.velocity = 0; // rad/s
    this.pendingYaw = 0;
    this.last = { x: 0, y: 0 };
    this.hover = false;

    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);

    window.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerup', this.onUp);
    window.addEventListener('pointercancel', this.onUp);
    window.addEventListener('blur', this.onUp);
  }

  canDrag(event) {
    if (event.pointerType === 'touch') return false;
    if (event.target && event.target.closest && event.target.closest(this.interactive)) {
      return false;
    }
    return this.hitTest(event.clientX, event.clientY);
  }

  onDown(event) {
    if (event.button !== 0 || !this.canDrag(event)) return;

    this.dragging = true;
    this.last.x = event.clientX;
    this.last.y = event.clientY;
    this.pendingYaw = 0;
    this.travel = 0;

    // Évite de sélectionner du texte pendant le glissement
    document.body.style.userSelect = 'none';
    this.setCursor('grabbing');
  }

  onMove(event) {
    if (!this.dragging) {
      const over = this.canDrag(event);
      if (over !== this.hover) {
        this.hover = over;
        this.setCursor(over ? 'grab' : '');
      }
      return;
    }

    const dx = event.clientX - this.last.x;
    const dy = event.clientY - this.last.y;
    this.last.x = event.clientX;
    this.last.y = event.clientY;
    this.travel += Math.abs(dx) + Math.abs(dy);

    this.pendingYaw += dx * this.sensitivity;
    this.tiltTarget = Math.max(
      -this.maxTilt,
      Math.min(this.maxTilt, this.tiltTarget + dy * this.sensitivity),
    );
  }

  onUp() {
    if (!this.dragging) return;
    this.dragging = false;
    this.pendingYaw = 0;

    // Un vrai glissement ne doit pas déclencher le clic (lien) qui suit le relâchement
    if (this.travel > 4) {
      this.justDragged = true;
      setTimeout(() => {
        this.justDragged = false;
      }, 50);
    }

    document.body.style.userSelect = '';
    this.setCursor(this.hover ? 'grab' : '');
  }

  setCursor(value) {
    if (this.onCursor) {
      this.onCursor(value);
    } else {
      document.body.style.cursor = value;
    }
  }

  // À appeler à chaque image. Renvoie la rotation Y (radians) à ajouter ce tour-ci.
  update(dt) {
    let yaw = 0;

    if (this.dragging) {
      yaw = this.pendingYaw;
      this.pendingYaw = 0;

      const velocity = dt > 0 ? yaw / dt : 0;
      this.velocity += (velocity - this.velocity) * 0.3;
      this.velocity = Math.max(
        -this.maxVelocity,
        Math.min(this.maxVelocity, this.velocity),
      );
    } else {
      // Élan après le relâchement, puis retour à plat de l'inclinaison
      yaw = this.velocity * dt;
      this.velocity *= Math.exp(-this.inertia * dt);
      if (Math.abs(this.velocity) < 0.01) this.velocity = 0;

      this.tiltTarget -= this.tiltTarget * (1 - Math.exp(-2 * dt));
    }

    this.tilt += (this.tiltTarget - this.tilt) * (1 - Math.exp(-12 * dt));
    return yaw;
  }

  destroy() {
    window.removeEventListener('pointerdown', this.onDown);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerup', this.onUp);
    window.removeEventListener('pointercancel', this.onUp);
    window.removeEventListener('blur', this.onUp);
    document.body.style.userSelect = '';
    this.setCursor('');
  }
}
